/**
 * Builders de payload para criação de Playbook (Brief 4 · Tela 1 · PR5).
 *
 * Não há endpoint de duplicação no backend — duplicar é client-side: lê-se o
 * playbook de origem e monta-se um `CreatePlaybookBody` espelhado, com o nome
 * sufixado " (cópia)" e status implícito `active` (o POST não aceita `draft` ·
 * R15 ao vivo). `steps`/`quickReplies` são normalizados para ARRAY PURO (o
 * serializer pode entregar `{ list }` legado — readList resolve ambos os shapes)
 * porque o POST grava array. Campos `string`-opcionais (checklistTemplateId/
 * personaId) só entram quando há valor: o DTO os valida como `@IsString` e
 * rejeita `null`.
 */

import { readList } from './playbook-labels';

import type { CreatePlaybookBody, PlaybookDto, PlaybookQuickReply, PlaybookStep } from './types';

/** Sufixo de cópia aplicado ao nome ao duplicar (visível na lista). */
export const COPY_SUFFIX = ' (cópia)';

/**
 * Monta o payload de criação espelhando um playbook existente (duplicação).
 * Copia todos os campos editáveis; o backend gera novo id/version e zera status
 * para `active`.
 */
export function buildDuplicatePayload(source: PlaybookDto): CreatePlaybookBody {
  const body: CreatePlaybookBody = {
    name: `${source.name}${COPY_SUFFIX}`,
    vertical: source.vertical,
    goal: source.goal,
    steps: readList<PlaybookStep>(source.steps),
    successCriteria: source.successCriteria,
    ethicalBoundaries: {
      redirectTopics: [...source.ethicalBoundaries.redirectTopics],
      toneLimits: [...source.ethicalBoundaries.toneLimits],
    },
    validationMode: source.validationMode,
    defaultOperationMode: source.defaultOperationMode,
    autoCloseable: source.autoCloseable,
    autoCloseableOutcomes: [...source.autoCloseableOutcomes],
    transitionPolicy: source.transitionPolicy,
    quickReplies: readList<PlaybookQuickReply>(source.quickReplies),
    proactiveAppetite: source.proactiveAppetite,
  };

  // Campos `@IsString @IsOptional` no DTO — omitir quando null (não aceita null).
  if (source.checklistTemplateId !== null) body.checklistTemplateId = source.checklistTemplateId;
  if (source.personaId !== null) body.personaId = source.personaId;
  if (source.outcomeReasonCatalog !== null) {
    body.outcomeReasonCatalog = source.outcomeReasonCatalog;
  }

  return body;
}

/** Verticais oferecidas no form de criação (espelham o enum do DTO). */
export const CREATE_VERTICALS: PlaybookDto['vertical'][] = [
  'support',
  'commercial',
  'retention',
  'collections',
  'triage',
  'onboarding',
  'satisfaction_survey',
  'legal',
  'healthcare',
  'other',
];

export interface CreateFormErrors {
  name?: string;
  goal?: string;
  successCriteria?: string;
}

/**
 * Valida os campos do form mínimo de criação contra as constraints do DTO
 * (name 2–120 · goal 5–10.000 · successCriteria ≤500, obrigatório). Retorna um mapa
 * de erros por campo (vazio = válido).
 */
export function validateCreateForm(fields: {
  name: string;
  goal: string;
  successCriteria: string;
}): CreateFormErrors {
  const errors: CreateFormErrors = {};
  const name = fields.name.trim();
  const goal = fields.goal.trim();
  const successCriteria = fields.successCriteria.trim();

  if (name.length < 2) errors.name = 'Informe um nome com ao menos 2 caracteres.';
  else if (name.length > 120) errors.name = 'O nome deve ter no máximo 120 caracteres.';

  if (goal.length < 5) errors.goal = 'Descreva o objetivo com ao menos 5 caracteres.';
  else if (goal.length > 10000) {
    errors.goal = 'O objetivo deve ter no máximo 10.000 caracteres.';
  }

  if (successCriteria.length === 0) errors.successCriteria = 'Informe o critério de sucesso.';
  else if (successCriteria.length > 500) {
    errors.successCriteria = 'O critério deve ter no máximo 500 caracteres.';
  }

  return errors;
}

/** Monta o payload de criação a partir do form mínimo (steps vazio, defaults no backend). */
export function buildCreatePayload(fields: {
  name: string;
  vertical: PlaybookDto['vertical'];
  goal: string;
  successCriteria: string;
}): CreatePlaybookBody {
  return {
    name: fields.name.trim(),
    vertical: fields.vertical,
    goal: fields.goal.trim(),
    steps: [],
    successCriteria: fields.successCriteria.trim(),
    ethicalBoundaries: { redirectTopics: [], toneLimits: [] },
  };
}
